import styled from 'styled-components';
import Color from 'color';
import { CircularProgressProps } from './props';
import { getComputedColor } from '@starsoft/common/utils';

/**
 * CircularProgress component that renders a circular progress indicator
 * using styled-components.
 *
 * @component
 * @param {CircularProgressProps} props
 * @param {number} props.progress - The progress percentage (0-100)
 * @param {string} props.pathColor - Custom color for the progress path
 * @param {string} props.trailColor - Custom color for the progress trail
 * @param {boolean} props.invert - Whether to invert the colors
 * @returns {React.ComponentType} Styled div component with circular progress styling
 */
export const CircularProgress = styled.div<CircularProgressProps>`
  ${({ progress, pathColor, trailColor, invert }) => {
    // Default primary color from MUI theme or fallback
    const primaryColor =
      'var(--mui-palette-primary-main, var(--primary-color))';

    // Calculate trail color based on props
    // Uses trailColor if provided, otherwise calculates based on invert flag
    const trail = trailColor
      ? trailColor
      : invert
        ? getComputedColor(primaryColor)
        : Color(getComputedColor(primaryColor)).alpha(0.5).toString();

    // Calculate path color based on props
    // Uses pathColor if provided, otherwise calculates based on invert flag
    const path = pathColor
      ? pathColor
      : invert
        ? Color(getComputedColor(primaryColor)).alpha(0.5).toString()
        : getComputedColor(primaryColor);

    // Return the CSS styles for the circular progress
    return `
      border-radius: 50%;
      width: 12px;
      height: 12px;
      background: conic-gradient(
        ${trail} ${progress}%, 0,
        ${path} ${100 - Math.ceil(progress)}%
      );
    `;
  }}
`;
